import { useState, useEffect } from "react";
import { JobCardForm } from "@/pages/jobcards/types";
import { employeesApi } from "@/services/api";

interface Employee {
  id: string;
  name: string;
  role: string;
  department: string;
  status: string;
  avatar: string;
  skills: string[];
}

interface Props {
  form: JobCardForm;
  onChange: (patch: Partial<JobCardForm>) => void;
}

function TeamSelector({
  label,
  employees,
  selected,
  onToggle,
  loading,
}: {
  label: string;
  employees: Employee[];
  selected: string[];
  onToggle: (id: string, name: string) => void;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-gray-400">
        <i className="ri-loader-4-line animate-spin text-amber-500" />
        Loading team members...
      </div>
    );
  }

  if (employees.length === 0) {
    return (
      <div className="flex items-center gap-2 p-3 rounded-xl bg-gray-50 border border-gray-100 text-sm text-gray-400">
        <i className="ri-user-search-line text-gray-300" />
        No staff found in this category
      </div>
    );
  }

  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-2">{label}</label>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {employees.map((emp) => {
          const isSelected = selected.includes(emp.id);
          return (
            <button
              key={emp.id}
              type="button"
              onClick={() => onToggle(emp.id, emp.name)}
              className={`flex items-center gap-2 p-2.5 rounded-xl border transition-all cursor-pointer text-left ${
                isSelected
                  ? "border-amber-400 bg-amber-50"
                  : "border-gray-100 bg-gray-50 hover:border-gray-200 hover:bg-white"
              }`}
            >
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                style={{
                  background: isSelected ? "#f59e0b" : "#e5e7eb",
                  color: isSelected ? "#fff" : "#6b7280",
                }}
              >
                {emp.avatar || emp.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
              </div>
              <div className="min-w-0 flex-1">
                <p className={`text-xs font-semibold truncate ${isSelected ? "text-amber-800" : "text-gray-700"}`}>
                  {emp.name}
                </p>
                <p className="text-xs text-gray-400 truncate capitalize">{emp.role.replace(/_/g, " ")}</p>
              </div>
              {isSelected && (
                <span className="w-4 h-4 flex items-center justify-center rounded-full bg-amber-500 flex-shrink-0">
                  <i className="ri-check-line text-white text-xs" />
                </span>
              )}
            </button>
          );
        })}
      </div>
      {selected.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {selected.map((id) => {
            const emp = employees.find((e) => e.id === id);
            if (!emp) return null;
            return (
              <span key={id} className="flex items-center gap-1 text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-medium">
                <span className="w-4 h-4 rounded-full bg-amber-500 flex items-center justify-center text-white text-xs font-bold">
                  {emp.name[0]}
                </span>
                {emp.name}
                <button type="button" onClick={() => onToggle(id, emp.name)} className="w-3 h-3 flex items-center justify-center hover:text-red-500 cursor-pointer">
                  <i className="ri-close-line text-xs" />
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function SectionTeamScope({ form, onChange }: Props) {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchEmployees = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await employeesApi.getAll() as { data?: Employee[]; employees?: Employee[] } | Employee[];
        const raw = Array.isArray(res) ? res : (res as { data?: Employee[]; employees?: Employee[] }).data || (res as { data?: Employee[]; employees?: Employee[] }).employees || [];
        // Map DB fields to our interface
        const mapped: Employee[] = raw.map((e: Record<string, unknown>) => ({
          id: String(e.id || ""),
          name: String(e.name || ""),
          role: String(e.role || e.designation || ""),
          department: String(e.department || ""),
          status: String(e.status || "active"),
          avatar: String(e.avatar || (e.name as string || "??").split(" ").map((w: string) => w[0]).join("").slice(0, 2).toUpperCase()),
          skills: Array.isArray(e.skills) ? e.skills as string[] : [],
        })).filter((e: Employee) => e.status === "active" || e.status === "Active");
        setEmployees(mapped);
      } catch {
        setError("Could not load staff list");
      } finally {
        setLoading(false);
      }
    };
    fetchEmployees();
  }, []);

  const fieldEmployees = employees.filter((e) =>
    ["Field Operations", "Lab", "field operations", "lab"].includes(e.department)
  );
  const officeEmployees = employees.filter((e) =>
    ["Office", "Management", "office", "management"].includes(e.department)
  );

  // Store selected IDs in fieldTeam / officeTeam
  const toggleField = (id: string, name: string) => {
    const cur = form.fieldTeam;
    // Store as "id::name" so we can display names later
    const key = `${id}::${name}`;
    onChange({ fieldTeam: cur.includes(key) ? cur.filter((n) => n !== key) : [...cur, key] });
  };

  const toggleOffice = (id: string, name: string) => {
    const cur = form.officeTeam;
    const key = `${id}::${name}`;
    onChange({ officeTeam: cur.includes(key) ? cur.filter((n) => n !== key) : [...cur, key] });
  };

  // Extract IDs from stored "id::name" format
  const selectedFieldIds = form.fieldTeam.map((k) => k.split("::")[0]);
  const selectedOfficeIds = form.officeTeam.map((k) => k.split("::")[0]);

  return (
    <>
      {/* Section 7 — Scope of Work */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
          <span className="w-5 h-5 flex items-center justify-center">
            <i className="ri-file-text-line text-amber-600 text-base" />
          </span>
          <h3 className="text-sm font-semibold text-gray-800">Section 7 — Scope of Work</h3>
        </div>
        <div className="p-5">
          <label className="block text-xs font-semibold text-gray-600 mb-1.5">
            Brief Description of Scope <span className="text-red-400">*</span>
          </label>
          <textarea
            value={form.scopeOfWork}
            onChange={(e) => onChange({ scopeOfWork: e.target.value })}
            rows={4}
            maxLength={1000}
            placeholder="Describe the scope of work, methodology, deliverables expected, and any special requirements..."
            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 resize-none transition-all"
          />
          <div className="flex items-center justify-between mt-1">
            <p className="text-xs text-gray-400">Describe methodology, deliverables, and special requirements</p>
            <span className="text-xs text-gray-400">{form.scopeOfWork.length}/1000</span>
          </div>
        </div>
      </div>

      {/* Section 8 — Field Team */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
          <span className="w-5 h-5 flex items-center justify-center">
            <i className="ri-user-location-line text-amber-600 text-base" />
          </span>
          <h3 className="text-sm font-semibold text-gray-800">Section 8 — Field Team Assignment</h3>
          {form.fieldTeam.length > 0 && (
            <span className="ml-auto text-xs bg-amber-500 text-white px-2 py-0.5 rounded-full font-semibold">
              {form.fieldTeam.length} assigned
            </span>
          )}
          <span className="ml-auto text-xs text-gray-400 flex items-center gap-1">
            <i className="ri-database-2-line text-xs" />Live from DB
          </span>
        </div>
        <div className="p-5">
          {error && (
            <div className="mb-3 flex items-center gap-2 p-2.5 rounded-lg bg-red-50 border border-red-100 text-xs text-red-600">
              <i className="ri-error-warning-line" />{error}
            </div>
          )}
          <TeamSelector
            label="Select Field Surveyors / Operators"
            employees={fieldEmployees}
            selected={selectedFieldIds}
            onToggle={toggleField}
            loading={loading}
          />
        </div>
      </div>

      {/* Section 9 — Office Team */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
          <span className="w-5 h-5 flex items-center justify-center">
            <i className="ri-computer-line text-amber-600 text-base" />
          </span>
          <h3 className="text-sm font-semibold text-gray-800">Section 9 — Office Team (CAD/GIS Processing)</h3>
          {form.officeTeam.length > 0 && (
            <span className="ml-auto text-xs bg-amber-500 text-white px-2 py-0.5 rounded-full font-semibold">
              {form.officeTeam.length} assigned
            </span>
          )}
          <span className="ml-auto text-xs text-gray-400 flex items-center gap-1">
            <i className="ri-database-2-line text-xs" />Live from DB
          </span>
        </div>
        <div className="p-5">
          <TeamSelector
            label="Select CAD / GIS / Office Staff"
            employees={officeEmployees}
            selected={selectedOfficeIds}
            onToggle={toggleOffice}
            loading={loading}
          />
        </div>
      </div>
    </>
  );
}
